import {
  Box,
  Button,
  Card,
  CardContent,
  Divider,
  Grid,
  TextField,
  Typography,
} from '@mui/material';
import { useEffect, useMemo, useState } from 'react';
import ImageSettings, { Size } from '../../../models/BlockComponentSettings/ImageSettings';
import BlockComponentImage from '../../../models/BlockComponentValues/BlockComponentImage';
import { fitParent } from '../../../utils/image';
import ModalComponent from '../../ModalComponent';
import ImageScaleSlider from '../ImageSettingsModal/components/ImageScaleSlider';
import ImagePreview from '../ImageSettingsModal/components/ImagePreview';
import ImageModal from './ImageModal';
import config from '../../../config';
import deepClone from '../../../utils/deepClone';

interface BasicImageSettingsModalProps {
  image?: BlockComponentImage;
  disabled: boolean;
  children?: JSX.Element;
  onChange: (image: BlockComponentImage) => void;
  showModal?: boolean;
  closeModal?: () => void;
  noScaleControls?: boolean;
  isEditing: boolean,
  hasLogo?: boolean,
  isCarousel?: boolean;
}

const BasicImageSettingsModal = ({
  closeModal,
  showModal: propShowModal,
  image: blockComponentImage,
  disabled,
  children,
  onChange,
  noScaleControls = false,
  isEditing = true,
  hasLogo = false,
  isCarousel = false,
}: BasicImageSettingsModalProps) => {
  // PDEV3-34【2】: edit on a deep copy so direct mutations (e.g. handleChangeZoom)
  // never leak to the source block — changes persist only via Apply (onChange).
  const [image, setImage] = useState<BlockComponentImage>(() => deepClone(blockComponentImage));
  const [imageSize, setImageSize] = useState<Size>({ width: 0, height: 0 });
  const [imageUpdated, setImageUpdated] = useState<number>(0);
  const [scale, setScale] = useState<number>(image?.settings?.scale ?? 1);

  const [localShowModal, setLocalShowModal] = useState(false);

  const modalShow = typeof propShowModal === 'boolean' ? propShowModal : localShowModal;
  const setModalShow = setLocalShowModal;

  // PDEV3-34【2】: re-clone from the source whenever the modal (re)opens, so
  // changes discarded via Close don't carry over to the next open.
  useEffect(() => {
    if (modalShow && blockComponentImage) {
      const fresh = deepClone(blockComponentImage);
      setImage(fresh);
      setScale(fresh?.settings?.scale ?? 1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modalShow]);

  // IMAGE SETTINGS
  const handleChangeImage = (value: string) => {
    setImage({ ...image, value });
  };
  const handleChangeZoom = (scale: number) => {
    image.settings.scale = scale;
    setScale(scale);
  };
  const handleChangeImageSettings = (settings: ImageSettings) => {
    setImage({ ...image, settings: { ...settings } });
  };

  const handleChangeAlternativeText = (alt: string) => {
    setImage({ ...image, alt });
  };

  const handleConfirm = () => {
    image.settings.scale = scale;
    onChange(image);
    setModalShow(false);
    if (closeModal) {
      closeModal();
    }
  };

  const cropSize = useMemo(() => {
    if (isCarousel) {
      return {
        width: Math.round(300 / (9 / 16)), // 4:3 aspect ratio for carousel
        height: 300,
      };
    }

    return fitParent({ width: 533, height: 300 }, imageSize, { width: 72, height: 40 });
  }, [imageSize, isCarousel]);

  const setDefaultSettings = (image: BlockComponentImage) => {
    const settings = ImageSettings.getDefault();
    setImage({ ...image, settings });
    setImageUpdated((imageUpdated + 1));
    setScale(1);
  };
  const handleDefaultImage = (image: BlockComponentImage) => {
    setImage({ ...image, value: config.defaultImageUrl });
    setImageUpdated((imageUpdated + 1));
  };

  const onCloseModal = () => {
    setModalShow(false);
    if (closeModal) {
      closeModal();
    }
  };

  return (
    <>
      {isEditing ? (
        <Box
          sx={{
            height: '100%',
            maxHeight: '100%',
            width: '100%',
            maxWidth: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: hasLogo ? 'left' : 'center',
          }}
          onClick={(event) => {
            if (disabled) return;
            setModalShow(true);
            event.stopPropagation();
          }}
        >
          {children}
        </Box>
      ) : (
        <Box
          sx={{
            height: '100%',
            maxHeight: '100%',
            width: '100%',
            maxWidth: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: hasLogo ? 'left' : 'center',
          }}
        >
          {children}
        </Box>
      )}
      {isEditing && (
        <ModalComponent
          size="xl"
          title="Image settings"
          confirmButtonText="Save"
          cancelButtonText="Close"
          onConfirm={handleConfirm}
          show={modalShow}
          closeModal={onCloseModal}
          centered
          animation={false}
          scrollable
          backdropClassName="z-index-3000"
          style={{ zIndex: 3001 }}
        >
          <div className="row">
            <div className="col-xl-5">
              <Card sx={{ width: '100%' }} className="mb-3">
                <CardContent>
                  <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                    <Box sx={{ height: '42px', display: 'flex', alignItems: 'center' }}>
                      <Typography className="col-form-label mb-1 text-sm" sx={{ fontWeight: '700' }}>
                        alt (alternative text) settings
                      </Typography>
                    </Box>
                    <TextField
                      label="Alternative text shown when image is not displayed"
                      value={image?.alt ?? ''}
                      onChange={(event) => handleChangeAlternativeText(event.target.value)}
                      size="small"
                      inputProps={{ translate: 'no', className: 'notranslate' }}
                    />
                  </Box>
                </CardContent>
              </Card>
              {!noScaleControls && (
                <Card sx={{ width: '100%' }}>
                  <CardContent>
                    <Grid item sx={{ width: '100%' }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Button
                          variant="contained"
                          color="primary"
                          size="small"
                          onClick={() => setDefaultSettings(image)}
                          sx={{ marginTop: '10px', marginBottom: '10px' }}
                        >
                          Reset to default
                        </Button>
                      </Box>
                    </Grid>
                    <Divider sx={{ width: '100%', my: 1 }} />
                    <ImageScaleSlider
                      value={scale}
                      handleChange={handleChangeZoom}
                    />
                  </CardContent>
                </Card>
              )}
            </div>

            <div className="col-xl-7">
              <div className="row g-2">
                <ImagePreview
                  image={image}
                  scale={scale}
                  crop={cropSize}
                  handleChange={(settings) => {
                    handleChangeImageSettings(settings);
                  }}
                  handleImageSizeChange={setImageSize}
                  key={imageUpdated}
                />
              </div>
              <div className="d-flex align-items-center justify-content-between gap-1">
                <div className="d-flex align-items-center gap-1">
                  <ImageModal
                    handleOnConfirm={handleChangeImage}
                    title="Select image"
                    absoluteButton={false}
                  />
                </div>
                <Button
                  variant="contained"
                  color="primary"
                  size="small"
                  onClick={() => handleDefaultImage(image)}
                  sx={{ marginTop: '10px', marginBottom: '10px', minWidth: 127 }}
                >
                  Reset to default image
                </Button>
              </div>
            </div>

          </div>
        </ModalComponent>
      )}
    </>
  );
};

export default BasicImageSettingsModal;
